import { useState } from "react";
import { Link } from "react-router-dom";
import { films } from "@/mocks/films";
import { specials } from "@/mocks/specials";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const FORM_SUBMIT_ADDR = "https://readdy.ai/api/form/db6fnojghk82orf0cl50";

const contactChannels = [
  { icon: "ri-mail-line", label: "合作邮箱", value: "hello@fairyflix.demo" },
  { icon: "ri-wechat-line", label: "微信公众号", value: "妖精网站 FairyFlix" },
  { icon: "ri-weibo-line", label: "官方微博", value: "@妖精网站" },
];

export default function AboutPage() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formMessage, setFormMessage] = useState("");

  useDocumentMeta(
    "妖精网站 - 关于我们与联系方式",
    "了解妖精网站的品牌理念、内容范围与联系方式，欢迎通过反馈表单与我们交流。"
  );

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("company_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setFormMessage("感谢你的反馈，我们会认真阅读每一条建议。");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "company_alt") return;
      if (typeof value === "string") {
        const trimmed = value.trim();
        if (trimmed === "") return;
        payload.append(key, trimmed);
      }
    });

    setStatus("submitting");
    setFormMessage("");

    try {
      const response = await fetch(FORM_SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isServerSuccess = response.ok && parsed?.code === "OK";
      const looksLikeSpam = String(serverMsg).toLowerCase().includes("spam");

      if (!isServerSuccess || looksLikeSpam) {
        setStatus("error");
        setFormMessage(
          serverMsg && !looksLikeSpam
            ? serverMsg
            : "提交未成功，请检查内容后重试，或稍后再试。"
        );
        return;
      }

      setStatus("success");
      setFormMessage("感谢你的反馈，我们会认真阅读每一条建议。");
      form.reset();
    } catch {
      setStatus("error");
      setFormMessage("网络异常，提交失败，请稍后重试。");
    }
  };

  return (
    <div className="w-full">
      <section className="border-b border-background-200 bg-background-100/60">
        <div className="mx-auto w-full max-w-[1200px] px-4 py-12 md:px-8 md:py-16">
          <nav className="mb-4 flex flex-wrap items-center gap-1 text-sm text-foreground-500">
            <Link to="/" className="transition-colors hover:text-primary-600">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-800">关于我们</span>
          </nav>
          <span className="mb-3 inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-xs font-semibold text-primary-700">
            <i className="ri-information-line" />
            关于妖精网站
          </span>
          <h1 className="font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
            关于妖精网站
          </h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            妖精网站（FairyFlix）是一个专注妖精题材影视作品的综合门户。我们以专业、友好的方式整合妖精主题的电影、电视剧资源，为喜爱妖精影视内容的观众提供便捷的搜索、分类浏览、剧情介绍与专题推荐体验。
          </p>
        </div>
      </section>

      <div className="mx-auto grid w-full max-w-[1200px] grid-cols-1 gap-8 px-4 py-12 md:px-8 md:py-16 lg:grid-cols-[1.1fr_1fr] lg:gap-14">
        {/* 左：介绍与联系方式 */}
        <div>
          <section className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
            <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
              <i className="ri-flag-2-line text-primary-600" />
              品牌理念
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600">
              妖精题材连接着人类对奇幻与浪漫的想象。我们希望成为妖精影视爱好者最信赖的入口——把散落各处的妖精电影、电视剧汇集起来，用清晰的分类、详细的剧情介绍与用心的主题策划，让你轻松找到下一部想看的作品。
            </p>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600">
              站内所有影视信息与资源均为演示数据，用于展示产品形态与浏览体验，不提供真实播放、下载或会员服务。
            </p>

            <div className="mt-6 grid grid-cols-3 gap-4 border-t border-background-200 pt-6">
              <div>
                <p className="font-heading text-2xl font-bold text-primary-600">{films.length}</p>
                <p className="mt-1 text-xs text-foreground-500">收录作品</p>
              </div>
              <div>
                <p className="font-heading text-2xl font-bold text-accent-600">
                  {specials.length}
                </p>
                <p className="mt-1 text-xs text-foreground-500">精选专题</p>
              </div>
              <div>
                <p className="font-heading text-2xl font-bold text-secondary-600">2</p>
                <p className="mt-1 text-xs text-foreground-500">内容分类</p>
              </div>
            </div>
          </section>

          <section className="mt-6 rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
            <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
              <i className="ri-contacts-book-2-line text-accent-600" />
              联系我们
            </h2>
            <ul className="mt-4 space-y-4">
              {contactChannels.map((channel) => (
                <li key={channel.label} className="flex items-center gap-3">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                    <i className={`${channel.icon} text-lg`} />
                  </span>
                  <div>
                    <p className="text-xs text-foreground-500">{channel.label}</p>
                    <p className="text-sm font-medium text-foreground-900">{channel.value}</p>
                  </div>
                </li>
              ))}
            </ul>
            <p className="mt-5 flex items-center gap-1.5 text-xs text-foreground-400">
              <i className="ri-error-warning-line" />
              以上联系方式均为演示信息
            </p>
          </section>
        </div>

        {/* 右：反馈表单 */}
        <section className="rounded-lg border border-background-200 bg-background-100/60 p-6 md:p-8">
          <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
            <i className="ri-chat-smile-2-line text-primary-600" />
            意见反馈
          </h2>
          <p className="mt-2 text-sm text-foreground-500">
            对妖精网站有任何建议或想推荐的作品？欢迎留言告诉我们。
          </p>

          <form
            id="fairy-feedback-form"
            data-readdy-form
            onSubmit={handleSubmit}
            className="mt-6 space-y-4"
          >
            <div>
              <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-foreground-700">
                姓名 / 昵称
              </label>
              <input
                id="name"
                name="name"
                type="text"
                required
                placeholder="请输入你的姓名或昵称"
                className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
              />
            </div>

            <div>
              <label
                htmlFor="email"
                className="mb-1.5 block text-sm font-medium text-foreground-700"
              >
                邮箱
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                placeholder="用于接收我们的回复"
                className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
              />
            </div>

            <div>
              <label
                htmlFor="message"
                className="mb-1.5 block text-sm font-medium text-foreground-700"
              >
                反馈内容
              </label>
              <textarea
                id="message"
                name="message"
                required
                maxLength={500}
                rows={5}
                placeholder="请描述你的建议或想推荐的作品（最多 500 字）"
                className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
              />
            </div>

            <input
              type="text"
              name="company_alt"
              className="feedback-form-field-alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
            />

            <button
              type="submit"
              disabled={status === "submitting"}
              className="flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {status === "submitting" ? (
                <>
                  <i className="ri-loader-4-line animate-spin" />
                  提交中…
                </>
              ) : (
                <>
                  <i className="ri-send-plane-fill" />
                  提交反馈
                </>
              )}
            </button>

            {formMessage && (
              <p
                className={`flex items-start gap-1.5 rounded-md px-4 py-3 text-sm ${
                  status === "success"
                    ? "bg-accent-100 text-accent-800"
                    : "bg-secondary-100 text-secondary-900"
                }`}
              >
                <i
                  className={
                    status === "success" ? "ri-checkbox-circle-line mt-0.5" : "ri-error-warning-line mt-0.5"
                  }
                />
                {formMessage}
              </p>
            )}
          </form>
        </section>
      </div>
    </div>
  );
}